/**
 * Client error reporter — batches browser errors and ships them to
 * POST /client-errors, which writes one Cloud Logging line per entry on the
 * `api` service (searchable by support: `dbg-logs api "<uid or route>"`).
 *
 * Framework-free and dependency-injected so it can be unit tested without
 * Firebase or a DOM; services/clientErrorReporting.ts wires it to apiRequest
 * and the window hooks.
 *
 * Invariants:
 *  - never throws, never blocks the caller (report() only enqueues);
 *  - never reports its own failures (a failed send is dropped, not retried or
 *    logged), so a broken endpoint cannot feed back into itself;
 *  - bounded: one report per distinct error per session (repeats only bump a
 *    counter while queued), and a hard per-session cap on everything sent;
 *  - sends only the declared fields — never form values or call-site context.
 */

export type ClientErrorLevel = 'error' | 'warning';
export type ClientErrorSource = 'handler' | 'window' | 'unhandledrejection' | 'boundary';

export interface ClientErrorPayload {
  message: string;
  stack?: string;
  route?: string;
  userAgent?: string;
  appVersion?: string;
  timestamp: string;
  level: ClientErrorLevel;
  source: ClientErrorSource;
  count?: number;
}

export interface ReporterDeps {
  /** Sends one batch. Rejections are swallowed. */
  send: (errors: ClientErrorPayload[]) => Promise<unknown>;
  /** Only send while signed in — the endpoint is authenticated. */
  isSignedIn: () => boolean;
  getRoute: () => string | undefined;
  userAgent?: string;
  appVersion?: string;
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => unknown;
}

export interface ReporterOptions {
  /** Max entries sent per page session. */
  maxPerSession?: number;
  /** Max entries per request (server accepts 20). */
  maxBatch?: number;
  /** Debounce before a batch goes out. */
  flushDelayMs?: number;
  /** Max entries held while waiting (e.g. not signed in yet). */
  maxQueued?: number;
}

const MAX_MESSAGE = 1_000;
const MAX_STACK = 4_000;

const clip = (s: string | undefined, max: number): string | undefined =>
  s === undefined ? undefined : s.length > max ? s.slice(0, max) : s;

/** Path only — the query string can hold invite/reset/magic-link tokens. */
export function routeOnly(href: string | undefined): string | undefined {
  if (!href) return undefined;
  return href.split(/[?#]/)[0];
}

/** Normalise anything thrown into message + stack. */
export function describeError(err: unknown): { message: string; stack?: string } {
  if (err instanceof Error) return { message: `${err.name}: ${err.message}`, stack: err.stack };
  if (typeof err === 'string') return { message: err };
  if (err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string') {
    return { message: (err as any).message, stack: typeof (err as any).stack === 'string' ? (err as any).stack : undefined };
  }
  try {
    return { message: `Non-error thrown: ${JSON.stringify(err)}`.slice(0, MAX_MESSAGE) };
  } catch {
    return { message: `Non-error thrown: ${String(err)}` };
  }
}

export class ClientErrorReporter {
  private queue: { key: string; payload: ClientErrorPayload }[] = [];
  private readonly seen = new Set<string>();
  private sentCount = 0;
  private flushScheduled = false;
  private inFlight = false;
  private readonly opts: Required<ReporterOptions>;
  private readonly now: () => number;
  private readonly schedule: (fn: () => void, ms: number) => unknown;

  constructor(private readonly deps: ReporterDeps, opts: ReporterOptions = {}) {
    this.opts = {
      maxPerSession: opts.maxPerSession ?? 50,
      maxBatch: opts.maxBatch ?? 20,
      flushDelayMs: opts.flushDelayMs ?? 5_000,
      maxQueued: opts.maxQueued ?? 20,
    };
    this.now = deps.now ?? Date.now;
    this.schedule = deps.schedule ?? ((fn, ms) => setTimeout(fn, ms));
  }

  report(err: unknown, meta: { level?: ClientErrorLevel; source: ClientErrorSource }): void {
    try {
      if (this.sentCount >= this.opts.maxPerSession) return;
      const { message, stack } = describeError(err);
      // Never report the reporter's own transport.
      if (message.includes('/client-errors')) return;
      const route = routeOnly(this.deps.getRoute());
      const firstFrame = stack?.split('\n').find((l) => l.trim().startsWith('at ')) ?? '';
      const key = `${message}|${route ?? ''}|${firstFrame.trim()}`;

      const queued = this.queue.find((q) => q.key === key);
      if (queued) {
        queued.payload.count = (queued.payload.count ?? 1) + 1;
        return;
      }
      if (this.seen.has(key)) return; // already sent this session
      if (this.queue.length >= this.opts.maxQueued) return;

      this.seen.add(key);
      this.queue.push({
        key,
        payload: {
          message: clip(message, MAX_MESSAGE) || '(no message)',
          stack: clip(stack, MAX_STACK),
          route,
          userAgent: this.deps.userAgent,
          appVersion: this.deps.appVersion,
          timestamp: new Date(this.now()).toISOString(),
          level: meta.level ?? 'error',
          source: meta.source,
        },
      });
      this.scheduleFlush();
    } catch {
      // Reporting must never be the thing that breaks the page.
    }
  }

  /** Sends what is queued now. Resolves once the send settles; never rejects. */
  async flush(): Promise<void> {
    this.flushScheduled = false;
    if (this.inFlight || this.queue.length === 0) return;
    if (!this.safe(() => this.deps.isSignedIn(), false)) return; // hold until signed in; next report reschedules

    const room = this.opts.maxPerSession - this.sentCount;
    if (room <= 0) {
      this.queue = [];
      return;
    }
    const batch = this.queue.splice(0, Math.min(this.opts.maxBatch, room)).map((q) => q.payload);
    this.sentCount += batch.length;
    this.inFlight = true;
    try {
      await this.deps.send(batch);
    } catch {
      // Dropped on purpose: no retry, no console output (see invariants).
    } finally {
      this.inFlight = false;
    }
    if (this.queue.length > 0) this.scheduleFlush();
  }

  /** For tests / diagnostics. */
  get stats(): { queued: number; sent: number } {
    return { queued: this.queue.length, sent: this.sentCount };
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    this.schedule(() => {
      void this.flush();
    }, this.opts.flushDelayMs);
  }

  private safe<T>(fn: () => T, fallback: T): T {
    try {
      return fn();
    } catch {
      return fallback;
    }
  }
}
